import { Entypo, Feather } from '@expo/vector-icons';
import { CustomFieldsModel, CustomFieldType, PrimaryKeyType } from '@w3lcome/types';
import { Text } from '_/components';
import { fontFamily } from '_/config/theme';
import themes from '_/constants/themes';
import { getCountryInfoFromCountryDial } from '_/helpers/formatPhoneNumber';
import { validate as validateCPF } from 'gerador-validador-cpf';
import { cardholderApi } from '_/services/api';
import { AsYouType } from 'libphonenumber-js';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  View,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  Switch,
} from 'react-native';
import CountryPicker, { CountryCode } from 'react-native-country-picker-modal';
import { showMessage } from 'react-native-flash-message';
import { IdNumberGuest } from '_/hooks/MeetingsProvider';

interface FoundGuest {
  name: string;
  cpf?: string;
  document?: string;
  email?: string;
}

interface PhoneFieldState {
  countryCode: string;
  countryLetters: CountryCode;
  countryName: string;
}

interface CardholderSearchAppProps {
  companyId: string;
  primaryKey?: PrimaryKeyType;
  lockedGuest?: IdNumberGuest | null;
  idNumberGuestFields?: Partial<CustomFieldsModel>[];
  onFoundAddToCheckin: (guest: FoundGuest) => void;
  onFoundRemoveFromCheckin: (guest: FoundGuest) => void;
  onNotFoundSetGuest: (guest: IdNumberGuest | null) => void;
  guestsToCheckinByHost?: FoundGuest[];
  isCredentialing?: boolean;
}

const CPF_REGEX = /^\d{3}\.\d{3}\.\d{3}-\d{2}$|^\d{11}$/;

const DEFAULT_PHONE_STATE: PhoneFieldState = {
  countryCode: '+55',
  countryLetters: 'BR',
  countryName: 'Brazil',
};

function formatCPF(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

function buildGuest(
  idNumber: string,
  extraFields: Record<string, string>,
  fields: Partial<CustomFieldsModel>[]
): IdNumberGuest {
  let email: string | undefined;
  let phone: string | undefined;

  for (const field of fields) {
    if (!field.id) continue;
    const value = extraFields[field.id]?.trim();
    if (!value) continue;
    if (field.type === CustomFieldType.EMAIL) email = value;
    else if (field.type === CustomFieldType.PHONE) phone = value;
  }

  return { idNumber, email, phone, extraFields };
}

export default function CardholderSearchApp({
  companyId,
  primaryKey = PrimaryKeyType.CPF,
  lockedGuest,
  idNumberGuestFields = [],
  onFoundAddToCheckin,
  onFoundRemoveFromCheckin,
  onNotFoundSetGuest,
  guestsToCheckinByHost = [],
  isCredentialing = false,
}: CardholderSearchAppProps) {
  const { t, i18n } = useTranslation();

  const isCPFPrimaryKey = primaryKey === PrimaryKeyType.CPF;
  const showForeignerSwitch = primaryKey === PrimaryKeyType.CPF || primaryKey === PrimaryKeyType.RG;

  const [isForeigner, setIsForeigner] = useState(false);
  const [idNumberInput, setIdNumberInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [extraFieldValues, setExtraFieldValues] = useState<Record<string, string>>({});

  const [phoneFieldStates, setPhoneFieldStates] = useState<Record<string, PhoneFieldState>>({});

  const [state, setState] = useState<'idle' | 'notFound' | 'found'>('idle');

  const isAdded = state === 'found' || state === 'notFound' || !!lockedGuest;

  const inputPlaceholder = isCPFPrimaryKey && !isForeigner ? '000.000.000-00' : primaryKey;

  function reset() {
    setState('idle');
    setExtraFieldValues({});
    setPhoneFieldStates({});
    setPhoneDisplayValues({});
  }

  function handleChangeId(value: string) {
    setState('idle');
    setIdNumberInput(isCPFPrimaryKey && !isForeigner ? formatCPF(value) : value);
  }

  function handleToggleForeigner(checked: boolean) {
    setIsForeigner(checked);
    setIdNumberInput('');
    reset();
    if (isAdded) onNotFoundSetGuest(null);
  }

  async function handleSearch() {
    if (!idNumberInput) return;

    if (isCPFPrimaryKey && !isForeigner && !validateCPF(idNumberInput)) {
      showMessage({ message: t('invalidField', { type: 'CPF' }), type: 'danger' });
      return;
    }

    if (!isCredentialing) {
      reset();
      onNotFoundSetGuest({ idNumber: idNumberInput });
      setState('notFound');
      return;
    }

    setLoading(true);
    reset();

    try {
      const { data } = await cardholderApi.getList({ IdNumber: idNumberInput, companyId });

      if (data?.length > 0) {
        const { FirstName, LastName, IdNumber, EMail } = data[0];
        const isCPF = CPF_REGEX.test(IdNumber);

        const guest: FoundGuest = {
          name: [FirstName, LastName].filter(Boolean).join(' '),
          email: EMail ?? undefined,
          ...(isCPF ? { cpf: IdNumber } : { document: IdNumber, isForeigner: true }),
        };

        onFoundAddToCheckin(guest);
        setState('found');
      } else {
        onNotFoundSetGuest({ idNumber: idNumberInput });
        setState('notFound');
      }
    } catch {
      onNotFoundSetGuest({ idNumber: idNumberInput });
      setState('notFound');
    } finally {
      setLoading(false);
    }
  }

  function getPhoneState(fieldId: string): PhoneFieldState {
    return phoneFieldStates[fieldId] ?? DEFAULT_PHONE_STATE;
  }

  const [phoneDisplayValues, setPhoneDisplayValues] = useState<Record<string, string>>({});

  function handlePhoneChange(fieldId: string, value: string, fieldState: PhoneFieldState) {
    setPhoneDisplayValues((prev) => ({ ...prev, [fieldId]: value }));

    const withCountryCode = value ? `${fieldState.countryCode}${value}` : '';
    const updated = { ...extraFieldValues, [fieldId]: withCountryCode };
    setExtraFieldValues(updated);
    onNotFoundSetGuest(buildGuest(idNumberInput, updated, idNumberGuestFields));
  }

  function handlePhoneBlur(fieldId: string, fieldState: PhoneFieldState) {
    const current = phoneDisplayValues[fieldId] ?? '';
    if (!current) return;

    const formatted = new AsYouType(fieldState.countryLetters).input(current);
    setPhoneDisplayValues((prev) => ({ ...prev, [fieldId]: formatted }));

    const withCountryCode = `${fieldState.countryCode}${formatted}`;
    const updated = { ...extraFieldValues, [fieldId]: withCountryCode };
    setExtraFieldValues(updated);
    onNotFoundSetGuest(buildGuest(idNumberInput, updated, idNumberGuestFields));
  }

  function handleCountrySelect(
    fieldId: string,
    callingCode: string,
    cca2: CountryCode,
    countryName: string,
    currentPhone: string
  ) {
    const newCode = `+${callingCode}`;

    setPhoneFieldStates((prev) => ({
      ...prev,
      [fieldId]: { countryCode: newCode, countryLetters: cca2, countryName },
    }));

    const digits = currentPhone.replace(/\D/g, '');
    const formatted = digits ? new AsYouType(cca2).input(digits) : '';

    setPhoneDisplayValues((prev) => ({ ...prev, [fieldId]: formatted }));

    const withCountryCode = `${newCode}${formatted}`;
    const updated = { ...extraFieldValues, [fieldId]: withCountryCode };
    setExtraFieldValues(updated);
    onNotFoundSetGuest(buildGuest(idNumberInput, updated, idNumberGuestFields));
  }

  function handleExtraFieldChange(fieldId: string, value: string) {
    const updated = { ...extraFieldValues, [fieldId]: value };
    setExtraFieldValues(updated);
    onNotFoundSetGuest(buildGuest(idNumberInput, updated, idNumberGuestFields));
  }

  function handleRemoveNotFound() {
    reset();
    setIdNumberInput('');
    onNotFoundSetGuest(null);
  }

  function handleRemoveFound(guest: FoundGuest) {
    onFoundRemoveFromCheckin(guest);
    if (guestsToCheckinByHost.length === 1) {
      reset();
      setIdNumberInput('');
    }
  }

  async function handleRemoveLockedGuest() {
    onNotFoundSetGuest(null);
  }

  function getFieldLabel(field: Partial<CustomFieldsModel>): string {
    const lang = i18n.language;
    return field.placeholder?.[lang] || field.placeholder?.['pt-BR'] || field.name || '';
  }

  function renderExtraField(field: Partial<CustomFieldsModel>) {
    if (!field.id) return null;
    const label = getFieldLabel(field);
    const isPhone = field.type === CustomFieldType.PHONE;
    const fieldState = getPhoneState(field.id);
    const displayValue = phoneDisplayValues[field.id] ?? ''; // ← usa displayValue, não extraFieldValues

    return (
      <View key={field.id} style={styles.fieldWrapper}>
        <Text style={styles.fieldLabel}>
          {label}
          {field.required && <Text style={styles.requiredMark}> *</Text>}
        </Text>

        {isPhone ? (
          <View style={styles.phoneRow}>
            <View style={styles.countryPickerWrapper}>
              <CountryPicker
                withFlag
                withFilter
                withCallingCode
                translation={i18n.language.startsWith('pt') ? 'por' : 'cym'}
                countryCode={fieldState.countryLetters}
                onSelect={(c) => {
                  const { name } = getCountryInfoFromCountryDial(`+${c.callingCode[0]}`);
                  handleCountrySelect(field.id!, c.callingCode[0], c.cca2, name, displayValue); // ← displayValue
                }}
              />
              <Text style={styles.callingCode}>{fieldState.countryCode}</Text>
            </View>
            <TextInput
              style={[styles.extraInput, styles.phoneInput]}
              placeholder={label}
              placeholderTextColor={themes.colors.graySecondary}
              keyboardType="phone-pad"
              textContentType="telephoneNumber"
              value={displayValue}
              onChangeText={(val) => handlePhoneChange(field.id!, val, fieldState)}
              onBlur={() => handlePhoneBlur(field.id!, fieldState)}
            />
          </View>
        ) : (
          <TextInput
            style={styles.extraInput}
            placeholder={label}
            placeholderTextColor={themes.colors.graySecondary}
            value={extraFieldValues[field.id] ?? ''}
            onChangeText={(val) => handleExtraFieldChange(field.id!, val)}
          />
        )}
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Text style={styles.label}>
        {isCPFPrimaryKey && isForeigner ? t('Document') : primaryKey}
      </Text>
      <View style={styles.row}>
        <TextInput
          style={[styles.input, isAdded && styles.inputDisabled]}
          value={idNumberInput}
          onChangeText={handleChangeId}
          placeholder={isForeigner ? t('Document') : t(inputPlaceholder)}
          placeholderTextColor={themes.colors.graySecondary}
          editable={!loading && !isAdded}
          returnKeyType="search"
          onSubmitEditing={handleSearch}
        />
        <TouchableOpacity
          style={[
            styles.addButton,
            (!idNumberInput || loading || isAdded) && styles.addButtonDisabled,
          ]}
          onPress={handleSearch}
          disabled={!idNumberInput || loading || isAdded}
        >
          {loading ? (
            <ActivityIndicator color={themes.colors.white} size="small" />
          ) : (
            <Feather name="plus" size={20} color={themes.colors.white} />
          )}
        </TouchableOpacity>
      </View>

      {showForeignerSwitch && (
        <View style={styles.foreignerRow}>
          <Switch
            value={isForeigner}
            onValueChange={handleToggleForeigner}
            disabled={isAdded}
            trackColor={{ true: themes.colors.primary }}
          />
          <Text style={styles.foreignerLabel}>{t('foreignerDocument')}</Text>
        </View>
      )}

      {lockedGuest && state !== 'notFound' && (
        <View style={styles.guestCard}>
          <Entypo name="v-card" size={16} color={themes.colors.primary} />
          <Text style={styles.guestCardText}>{lockedGuest.idNumber}</Text>
          <TouchableOpacity onPress={handleRemoveLockedGuest} style={styles.removeBtn}>
            <Feather name="x" size={16} color={themes.colors.danger} />
          </TouchableOpacity>
        </View>
      )}

      {state === 'notFound' && (
        <View>
          <View style={styles.guestCard}>
            <Entypo name="v-card" size={16} color={themes.colors.primary} />
            <Text style={styles.guestCardText}>{idNumberInput}</Text>
            <TouchableOpacity onPress={handleRemoveNotFound} style={styles.removeBtn}>
              <Feather name="x" size={16} color={themes.colors.danger} />
            </TouchableOpacity>
          </View>

          {idNumberGuestFields.length > 0 && (
            <View style={styles.extraFieldsContainer}>
              {idNumberGuestFields
                .slice()
                .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
                .map(renderExtraField)}
            </View>
          )}
        </View>
      )}

      {guestsToCheckinByHost.length > 0 && (
        <View style={styles.foundList}>
          {guestsToCheckinByHost.map((guest, idx) => (
            <View key={idx} style={styles.guestCard}>
              <Feather name="user" size={14} color={themes.colors.primary} />
              <View style={styles.foundGuestInfo}>
                <Text style={styles.guestCardText}>{guest.name}</Text>
                {guest.cpf && <Text style={styles.guestCardSub}>CPF: {guest.cpf}</Text>}
                {guest.document && <Text style={styles.guestCardSub}>Doc: {guest.document}</Text>}
                {guest.email && <Text style={styles.guestCardSub}>{guest.email}</Text>}
              </View>
              <TouchableOpacity onPress={() => handleRemoveFound(guest)} style={styles.removeBtn}>
                <Feather name="x" size={16} color={themes.colors.danger} />
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { marginBottom: 16 },
  label: {
    color: themes.colors.darkQuaternary,
    fontFamily: fontFamily.medium,
    fontSize: 14,
    marginBottom: 6,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  input: {
    flex: 1,
    borderWidth: 1,
    borderColor: themes.colors.lightSecondary,
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
    color: themes.colors.darkSecondary,
    fontFamily: fontFamily.regular,
  },
  inputDisabled: {
    backgroundColor: themes.colors.lightPrimary,
    color: themes.colors.graySecondary,
  },
  addButton: {
    backgroundColor: themes.colors.primary,
    borderRadius: 6,
    paddingHorizontal: 16,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 64,
  },
  addButtonDisabled: { opacity: 0.4 },
  addButtonText: {
    color: themes.colors.white,
    fontFamily: fontFamily.medium,
    fontSize: 14,
  },
  foreignerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    gap: 8,
  },
  foreignerLabel: {
    color: themes.colors.darkQuaternary,
    fontFamily: fontFamily.regular,
    fontSize: 13,
  },
  guestCard: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: themes.colors.lightSecondary,
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginTop: 8,
    gap: 8,
  },
  guestCardText: {
    flex: 1,
    color: themes.colors.darkSecondary,
    fontFamily: fontFamily.medium,
    fontSize: 14,
  },
  guestCardSub: {
    color: themes.colors.graySecondary,
    fontFamily: fontFamily.regular,
    fontSize: 11,
  },
  foundGuestInfo: { flex: 1 },
  removeBtn: { padding: 4 },
  extraFieldsContainer: { marginTop: 8 },
  fieldWrapper: { marginBottom: 8 },
  fieldLabel: {
    color: themes.colors.darkQuaternary,
    fontFamily: fontFamily.medium,
    fontSize: 13,
    marginBottom: 4,
  },
  requiredMark: {
    color: themes.colors.danger,
    fontFamily: fontFamily.medium,
    fontSize: 13,
  },
  extraInput: {
    borderWidth: 1,
    borderColor: themes.colors.lightSecondary,
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
    color: themes.colors.darkSecondary,
    fontFamily: fontFamily.regular,
  },
  foundList: { marginTop: 4 },
  phoneRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: themes.colors.lightSecondary,
    borderRadius: 6,
    overflow: 'hidden',
  },
  countryPickerWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 2,
    backgroundColor: themes.colors.lightPrimary,
  },
  callingCode: {
    color: themes.colors.darkSecondary,
    fontFamily: fontFamily.medium,
    fontSize: 14,
  },
  phoneInput: {
    flex: 1,
    borderWidth: 0,
    marginLeft: 8,
  },
});
